/**
 * "Fix with AI" / "Ask AI" from the project (a preview's build error, a file's errors): hands `prompt` to the
 * project chat as a `pendingMessage` (`{id, text}`, VfsChatPanel -> the chatbot widget), which the widget sends into
 * the conversation whenever its `id` is new, also mid-conversation. Its `initialMessage` is not for this: the widget
 * ignores it once the conversation has started (an open `?conversationId=` included), so a click did nothing.
 *
 * `prefill: true` (the Data tab's "Ask the AI to add it"): the text is NOT sent, it is written into the chat's input
 * for the person to finish and send themselves. The widget has no prop for a draft, so it goes as a `pendingDraft`
 * (`{id, text}`, via `setPendingDraft`) to VfsChatPanel, which writes it into the widget's own textarea
 * (`prefillChatInput` below) once the widget is there. Without a `setPendingDraft` nothing happens: a prefill never
 * falls back to sending.
 *
 * With Chat turned off (sandbox-first) the chat panel is not even mounted: Chat is turned on, and the message waits
 * in the host's state until the panel mounts and sends (or writes) it.
 *
 * Returns the `{id, text}` handed over, or null when there was nothing to send.
 */
let counter = 0;

export function askAiInChat(prompt, { setPendingMessage, setPendingDraft, prefill = false, chatOn, turnOnChat, onChatTurnedOn, showChatPane } = {}) {
  const raw = typeof prompt === 'string' ? prompt : '';
  if (!raw.trim()) { return null; }
  if (prefill && !setPendingDraft) { return null; }
  counter += 1;
  // A draft keeps its trailing space ("…service: "): the person goes on typing after it.
  const message = { id: `${prefill ? 'prefill' : 'fix-with-ai'}-${Date.now()}-${counter}`, text: prefill ? raw.trimStart() : raw.trim() };
  if (!chatOn && turnOnChat) {
    turnOnChat(true);
    onChatTurnedOn?.();
  }
  showChatPane?.();
  if (prefill) {
    setPendingDraft(message);
  } else {
    setPendingMessage?.(message);
  }
  return message;
}

/**
 * Writes `text` into the chat widget's input inside `container` (the composer is the last textarea there: a message
 * being edited in the transcript comes before it) and focuses it with the caret at the end. The widget's textarea is
 * a controlled React input, so the value goes through the native setter plus an `input` event, which React turns
 * into the widget's own onChange. Returns false when there is no input yet (the caller tries again).
 */
export function prefillChatInput(container, text) {
  const inputs = container?.querySelectorAll?.('textarea');
  const input = inputs?.length ? inputs[inputs.length - 1] : null;
  if (!input || input.disabled) { return false; }
  const setter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value')?.set;
  if (setter) { setter.call(input, text); } else { input.value = text; }
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.focus();
  try { input.setSelectionRange(text.length, text.length); } catch { /* not selectable */ }
  return true;
}
